import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { LEGAL_PAGES } from '../siteConfig'

// Both privacy routes stay live (store listings link to them); either one
// highlights the Privacy tab.
const PRIVACY_ROUTES = ['/privacy-policy', '/privacypolicy']
const isActiveTab = (to, pathname) =>
    to === pathname || (to === '/privacy-policy' && PRIVACY_ROUTES.includes(pathname))

// Headings like "2.1 Personal Information" are shown indented in the contents.
const SUB_HEADING = /^\s*\d+\.\d+/

// Builds the contents list from the page's existing <h4> headings, so the
// legal text itself needs no changes. Uses buttons + scrollIntoView because
// "#anchor" links would be treated as routes by HashRouter.
const useTableOfContents = (articleRef, pathname) => {
    const [items, setItems] = useState([])
    const [activeId, setActiveId] = useState('')

    useEffect(() => {
        const headings = Array.from(articleRef.current.querySelectorAll('h4'))
        headings.forEach((h, i) => { if (!h.id) h.id = `section-${i + 1}` })
        setItems(headings.map((h) => ({
            id: h.id,
            label: h.textContent.replace(/\s+/g, ' ').replace(/:\s*$/, '').trim(),
            sub: SUB_HEADING.test(h.textContent),
        })))

        const observer = new IntersectionObserver((entries) => {
            const visible = entries.filter((e) => e.isIntersecting)
            if (visible.length) setActiveId(visible[0].target.id)
        }, { rootMargin: '-90px 0px -65% 0px' })
        headings.forEach((h) => observer.observe(h))
        return () => observer.disconnect()
    }, [articleRef, pathname])

    return { items, activeId }
}

const LegalPage = ({ children }) => {
    const articleRef = useRef(null)
    const { pathname } = useLocation()
    const { items, activeId } = useTableOfContents(articleRef, pathname)
    const showToc = items.length >= 3

    const scrollTo = (id) => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <>
            <Header />
            <main>
                <div className="legal-hero">
                    <div className="egg-bg a" />
                    <div className="wrap">
                        <span className="eyebrow">Legal &amp; Support</span>
                        <nav className="legal-tabs" aria-label="Policies">
                            {LEGAL_PAGES.map(({ to, label }) => (
                                <Link key={to} to={to} className={isActiveTab(to, pathname) ? 'active' : ''}>{label}</Link>
                            ))}
                        </nav>
                    </div>
                </div>
                <div className="legal-body">
                    <div className={`wrap legal-layout${showToc ? '' : ' no-toc'}`}>
                        {showToc && (
                            <aside className="legal-toc" aria-label="On this page">
                                <h6>On this page</h6>
                                <ul>
                                    {items.map((item) => (
                                        <li key={item.id}>
                                            <button
                                                type="button"
                                                className={`${item.sub ? 'sub' : ''} ${item.id === activeId ? 'active' : ''}`}
                                                onClick={() => scrollTo(item.id)}
                                            >
                                                {item.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                        )}
                        <article className="legal-article" ref={articleRef}>
                            {children}
                        </article>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    )
}

export default LegalPage
